import {useManagedCampaignType} from "@/store/campaignManagement";
import type {
  TCampaignApplicationAccepted,
  TCampaignApplicationReporting,
  TInstagramContent,
  TInstagramMedia,
} from "@/store/types";
import {getStoryContentsFromCampaignApplication, getUserFromCampaignApplication} from "@/utils/campaignUtils";

const useInfluencerContent = (campaignApplication?: TCampaignApplicationAccepted | TCampaignApplicationReporting) => {
  const campaignType = useManagedCampaignType();

  const user = getUserFromCampaignApplication({campaignApplication, campaignType});
  const buyoutsPerContent = campaignApplication?.tokens_per_content ?? undefined;

  const ugcContent: TInstagramContent[] =
    campaignApplication?.ugc
      ?.filter(ugcItem => ugcItem.ugc_media)
      .map(item => ({
        applicationId: item.ugc_media!.campaign_application,
        buyoutsPerContent,
        id: item.id,
        image: item.ugc_media!.thumbnail_file!,
        instagram_media: {
          created: item.ugc_media!.created,
          featured: item.ugc_media!.featured!,
          id: item.ugc_media!.id,
          liked: item.ugc_media!.liked,
          media_file: item.ugc_media!.media_file,
          media_product_type: "UGC",
          media_type: "VIDEO",
          thumbnail_file: item.ugc_media!.thumbnail_file,
        },
        ugcInformation: {
          remainingRevisions: item.revision_remaining,
          revisionStatus: item.ugc_media!.status,
          revisionReason: item.ugc_media!.revision_reason,
          videoTitle: item.ugc_content!.name,
        },
        user,
      })) ?? [];

  const posts: TInstagramContent[] = campaignApplication?.instagram_media?.has_media
    ? campaignApplication.instagram_media.posts.map(item => ({
        applicationId: campaignApplication.id,
        buyoutsPerContent,
        caption: item.caption,
        id: item.id,
        image: item.thumbnail_file! ?? item.thumbnail_url!,
        instagram_media: item as TInstagramMedia,
        user,
      }))
    : (campaignApplication?.posts?.map(item => ({
        applicationId: campaignApplication.id,
        buyoutsPerContent,
        id: item.id,
        image: item.image!,
        instagram_media: {
          featured: item.featured,
          id: item.id,
          media_file: item.image,
          media_product_type: "POST",
          media_type: "IMAGE",
          thumbnail_file: item.image,
        },
        user,
      })) ?? []);

  const reels: TInstagramContent[] = campaignApplication?.instagram_media?.has_media
    ? campaignApplication.instagram_media.reels.map(item => ({
        applicationId: campaignApplication.id,
        buyoutsPerContent,
        caption: item.caption,
        id: item.id,
        image: item.thumbnail_file! ?? item.thumbnail_url!,
        instagram_media: item as TInstagramMedia,
        user,
      }))
    : (campaignApplication?.reels?.map(item => ({
        applicationId: campaignApplication.id,
        buyoutsPerContent,
        id: item.id,
        image: item.image!,
        instagram_media: {
          featured: item.featured,
          id: item.id,
          media_file: item.image!,
          media_product_type: "REEL",
          media_type: "IMAGE",
          thumbnail_file: item.image!,
        },
        user,
      })) ?? []);

  const stories: TInstagramContent[] = getStoryContentsFromCampaignApplication({
    campaignApplication,
    campaignType,
  });

  const tiktokContent: TInstagramContent[] = (campaignApplication?.tiktok ?? []).map(item => {
    const isCarousel = item.post_images!.length > 1;
    const isSingularPost = item.post_images!.length === 1;

    return {
      applicationId: campaignApplication?.id,
      buyoutsPerContent,
      caption: item.caption,
      id: item.id,
      image: item.thumbnail!.url!,
      instagram_media: {
        children: isCarousel
          ? item.post_images!.map((image, idx) => ({
              id: idx,
              media_file: image.url,
              media_id: "",
              media_product_type: "TIKTOK",
              media_type: "IMAGE",
              thumbnail_file: image.url,
            }))
          : [],
        comments_count: item.comment_count,
        created: item.created,
        featured: item.featured,
        id: item.id,
        last_copied: item.last_copied,
        like_count: item.digg_count,
        liked: item.liked,
        media_file: isCarousel || isSingularPost ? item.thumbnail!.url : item.video!.url,
        media_product_type: "TIKTOK",
        media_type: isCarousel ? "CAROUSEL_ALBUM" : isSingularPost ? "IMAGE" : "VIDEO",
        media_url: item.thumbnail!.url,
        permalink: item.permalink,
        play_count: item.play_count,
        purchased: item.purchased,
        reach_count: item.reached_audience,
        save_count: item.collect_count,
        share_count: item.share_count,
        thumbnail_file: item.thumbnail!.url,
        thumbnail_url: item.thumbnail!.url,
        video_code: item.video_code,
      },
      user,
    };
  });

  return {posts, reels, stories, tiktokContent, ugcContent, user};
};

export {useInfluencerContent};
